// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React, { useId } from 'react';

interface ProviderAccessDocumentErrorIllustrationProps {
  /** Square size in px (width and height). Defaults to 100. */
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
}

/**
 * "Document error" illustration — a 100×100 red document-with-warning
 * graphic shown on the Letter of Authority rejection screen. Square
 * aspect; scales cleanly at any size via the {@link size} prop.
 */
export const ProviderAccessDocumentErrorIllustration: React.FC<
  ProviderAccessDocumentErrorIllustrationProps
> = ({
  size = 100,
  className,
  style,
  'aria-label': ariaLabel = 'Document rejected',
}) => {
  const id = useId();
  const clipId = `pa-doc-err-clip-${id}`;
  const maskId = `pa-doc-err-mask-${id}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={style}
    >
      <rect width="100" height="100" rx="50" fill="#96040E" />
      <g clipPath={`url(#${clipId})`}>
        <mask
          id={maskId}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="22"
          y="22"
          width="56"
          height="56"
        >
          <path d="M78 22H22V78H78V22Z" fill="white" />
        </mask>
        <g mask={`url(#${maskId})`}>
          <path d="M57.0001 75.0837H43.0001C30.3301 75.0837 24.9167 69.6703 24.9167 57.0003V43.0003C24.9167 30.3303 30.3301 24.917 43.0001 24.917H54.6667C55.6234 24.917 56.4167 25.7103 56.4167 26.667C56.4167 27.6237 55.6234 28.417 54.6667 28.417H43.0001C32.2434 28.417 28.4167 32.2437 28.4167 43.0003V57.0003C28.4167 67.757 32.2434 71.5837 43.0001 71.5837H57.0001C67.7567 71.5837 71.5834 67.757 71.5834 57.0003V45.3337C71.5834 44.377 72.3767 43.5837 73.3334 43.5837C74.2901 43.5837 75.0834 44.377 75.0834 45.3337V57.0003C75.0834 69.6703 69.6701 75.0837 57.0001 75.0837Z" fill="white" />
          <path d="M73.3334 47.0851H64.0001C56.0201 47.0851 52.9167 43.9818 52.9167 36.0018V26.6684C52.9167 25.9684 53.3367 25.3151 53.9901 25.0584C54.6434 24.7784 55.3901 24.9418 55.9034 25.4318L74.5701 44.0984C75.0601 44.5884 75.2234 45.3585 74.9434 46.0118C74.6634 46.6651 74.0334 47.0851 73.3334 47.0851ZM56.4167 30.8918V36.0018C56.4167 42.0218 57.9801 43.5851 64.0001 43.5851H69.1101L56.4167 30.8918Z" fill="white" />
        </g>
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="56" height="56" fill="white" transform="translate(22 22)" />
        </clipPath>
      </defs>
    </svg>
  );
};
